import { ScoringPriorityField } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { EventInfoData } from '@shared/utils/eventInfo';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useUpdateAccommodationsSortOrder } from '@apps/admin/common/hooks/useUpdateAccommodationsSortOrder';
import { useEventVenueDialogTelemetry } from '../components/EventVenueDialog/EventVenueDialog.telemetry';
import { EventVenueState } from '../components/EventVenueDialog/useEventVenueDialog';
import { useTravelPlacesPricingAndBookingDetails } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails/useTravelPlacesPricingAndBookingDetails';
import { useEffect, useMemo, useRef } from 'react';
import { useAccommodationPropertiesWithCalculatedPrice } from '@shared/hooks/useSearchExtendedAccommodationProperties';
import { VenueDialogAction } from './useEventVenueDialogReducer';
import { transformAccommodationPlaceToProperty } from '../components/EventVenueDialog/EventVenueDialog.utils';
import { addAction } from '@shared/utils/logger';
import { parseNetworkErrorDetails } from '@shared/utils/parseNetworkErrorDetails';
import { searchRadiiKm } from '../components/EventVenueDialog/EventVenueDialog';
import { AutoPopulationApiV4Response, CallAutoPopulateAccommodationPlacesV4Payload } from '@shared/utils/accommodations/autoPopulation/types';

interface UseVenueRevealHotelMatches
  extends Readonly<{
    eventInfo?: EventInfoData;
    eventHandle?: string;
    destination: google.maps.LatLngLiteral;
    eventVenueState: EventVenueState;
    scoringPriorityFields: ScoringPriorityField[];
    searchRadiiKm: typeof searchRadiiKm;
    dispatch: React.Dispatch<VenueDialogAction>;
    goToHotels: () => void;
    goToNoHotels: () => void;
    callAutoPopulateAccommodationPlacesV4: (payload: CallAutoPopulateAccommodationPlacesV4Payload) => Promise<ReadonlyArray<AutoPopulationApiV4Response>>;
    onCompleteAutoPopulation: (autoPopulatedAccommodationPlaceIds: ReadonlyArray<string>) => void;
  }> {}

export const useVenueRevealHotelMatches = ({
  eventInfo,
  eventHandle,
  destination,
  eventVenueState,
  scoringPriorityFields,
  searchRadiiKm,
  dispatch,
  goToHotels,
  goToNoHotels,
  callAutoPopulateAccommodationPlacesV4,
  onCompleteAutoPopulation,
}: UseVenueRevealHotelMatches) => {
  const currentRadiusIndexRef = useRef(0);
  const hasSetAccommodationsHotels = useRef<boolean | undefined>();

  const telemetry = useEventVenueDialogTelemetry();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { updateSortOrder } = useUpdateAccommodationsSortOrder(() => updateWebsitePreview());

  const { data, loading: isLoading, fetchTravelPricingData } = useTravelPlacesPricingAndBookingDetails([], 'accommodationPlace', true);

  const accommodationCatalogProperties = useMemo(() => transformAccommodationPlaceToProperty(data?.travelPlacesPricingAndBookingDetails.accommodationPlaces), [data]);

  const accommodationPropertyWithCalculatedPrice = useAccommodationPropertiesWithCalculatedPrice(accommodationCatalogProperties, destination);

  useEffect(() => {
    if (!isLoading && accommodationPropertyWithCalculatedPrice.length > 0 && !hasSetAccommodationsHotels.current) {
      dispatch({ type: 'AUTO_POPULATE_ACCOMMODATIONS_PLACES_SUCCESS', accommodationPropertyWithCalculatedPrice });

      const placeIds = accommodationPropertyWithCalculatedPrice.map(h => h.accommodationPlaceId ?? '').filter(Boolean);
      updateSortOrder({ accommodationsPlaceIds: placeIds, action: 'add' });

      hasSetAccommodationsHotels.current = true;
    }
  }, [accommodationPropertyWithCalculatedPrice, isLoading, dispatch, scoringPriorityFields, updateSortOrder]);

  const mapPlaceIds = useEventCallback((places: ReadonlyArray<AutoPopulationApiV4Response>) => places.map(place => place.accommodationPlaceId).filter(Boolean));

  const handlePopulateSuccess = useEventCallback((places: ReadonlyArray<AutoPopulationApiV4Response>) => {
    const placeIds = mapPlaceIds(places);
    fetchTravelPricingData(placeIds);
    onCompleteAutoPopulation(placeIds);
  });

  // On failed to auto populate no retry, might possible api already auto populated hotels but returned error
  const handlePopulateFailure = useEventCallback(() => {
    onCompleteAutoPopulation([]);
    goToNoHotels();
    dispatch({ type: 'AUTO_POPULATE_ACCOMMODATIONS_PLACES_ERROR' });
    updateWebsitePreview();
  });

  const callaAutoPopulateApi = useEventCallback(async (searchRadiusKm: number, reasonForRetry: 'failure' | 'radius_increase') => {
    addAction('retrySearchApiCall', { message: 'Auto populate API call', context: 'EventVenueDialog', eventHandle, destination, reasonForRetry });
    return callAutoPopulateAccommodationPlacesV4({
      eventId: eventInfo?.eventId,
      location: { latitude: destination.lat, longitude: destination.lng },
      scoringPriorityFields,
      context: 'EventVenueDialog',
      searchRadiusKm,
    });
  });

  const handlePopulateRetryApiCall = useEventCallback(async () => {
    while (currentRadiusIndexRef.current < searchRadiiKm.length) {
      const searchRadiusKm = searchRadiiKm[currentRadiusIndexRef.current];

      const places = await callaAutoPopulateApi(searchRadiusKm, 'radius_increase');
      const placeIds = mapPlaceIds(places);

      if (placeIds?.length) {
        handlePopulateSuccess(places);
        return;
      }

      currentRadiusIndexRef.current += 1;
    }

    // All radius attempts exhausted — show error
    handlePopulateFailure();
  });

  return useEventCallback(async () => {
    telemetry.revealHotelMatches(scoringPriorityFields, eventVenueState, eventHandle);
    goToHotels();

    dispatch({ type: 'AUTO_POPULATE_ACCOMMODATIONS_PLACES_STARTED' });

    try {
      const places = await callAutoPopulateAccommodationPlacesV4({
        eventId: eventInfo?.eventId,
        location: { latitude: destination.lat, longitude: destination.lng },
        scoringPriorityFields,
        context: 'EventVenueDialog',
        searchRadiusKm: searchRadiiKm[0],
      });

      const placeIds = mapPlaceIds(places);
      if (placeIds?.length) {
        handlePopulateSuccess(places);
        return;
      }

      // Skip first/default index where its already searched with first time
      currentRadiusIndexRef.current += 1;

      // Retry on empty results
      await handlePopulateRetryApiCall();
    } catch (error) {
      // There is chance auto populate api already added hotels if error is timed out, So we should prevent retry for timeout error.
      const { isTimeout } = parseNetworkErrorDetails(error);
      if (isTimeout) {
        handlePopulateFailure();
        return;
      }

      // Retry once on api failed
      try {
        const places = await callaAutoPopulateApi(searchRadiiKm[0], 'failure');
        const placeIds = mapPlaceIds(places);
        if (placeIds?.length) {
          handlePopulateSuccess(places);
        } else {
          handlePopulateFailure();
        }
      } catch {
        handlePopulateFailure();
      }
    }
  });
};
